import { combineReducers, configureStore, PreloadedState, ThunkMiddleware } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { createLogger } from 'redux-logger';
import createSagaMiddleware from 'redux-saga';

import * as middlewares from '@src/middlewares';
import sagas from '@src/sagas';

import { accountsReducer } from './accounts';
import { buyReducer } from './buy';
import { cardsReducer } from './cards';
import { globalReducer } from './global';
import { loginReducer } from './login';
import { notificationsReducer } from './notifications';
import { operationBlockReducer } from './operationBlock';
import { sellReducer } from './sell';
import { transactionsReducer } from './transactions';
import { userReducer } from './user';
import { walletReducer } from './wallet';
import { withdrawReducer } from './withdraw';

let WEBPACK_LOCAL_CONFIG;
try {
  /* eslint-disable-next-line global-require */
  WEBPACK_LOCAL_CONFIG = require('../../webpack-local.config');
} catch (e) {
  WEBPACK_LOCAL_CONFIG = {};
}

export const rootReducer = combineReducers({
  accounts: accountsReducer,
  login: loginReducer,
  buy: buyReducer,
  cards: cardsReducer,
  global: globalReducer,
  sell: sellReducer,
  transactions: transactionsReducer,
  user: userReducer,
  wallet: walletReducer,
  withdraw: withdrawReducer,
  notifications: notificationsReducer,
  operationBlock: operationBlockReducer,
});

export type AppState = ReturnType<typeof rootReducer>;
export const userSelector = (state: AppState): typeof state.user => state.user;

export { accountsActions, type AccountsTypes } from './accounts';
export { loginActions, type LoginTypes } from './login';
export { buyActions, type BuyTypes } from './buy';
export { cardsActions, type CardsTypes } from './cards';
export { globalActions, type GlobalTypes } from './global';
export { sellActions, type SellTypes } from './sell';
export { transactionsActions, type TransactionsTypes } from './transactions';
export { userActions, type UserTypes } from './user';
export { walletActions, type WalletTypes } from './wallet';
export { withdrawActions, type WithdrawTypes } from './withdraw';
export { notificationsActions, type NotificationsTypes } from './notifications';
export { operationBlockActions } from './operationBlock';

export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = (): AppDispatch => useDispatch<AppDispatch>();

export const useStore = (): [AppState, AppDispatch] => {
  const store = useSelector((state: AppState) => state);
  const dispatch = useAppDispatch();
  return [store, dispatch];
};

const sagaMiddleware = createSagaMiddleware();

export const setupStore = (preloadedState?: PreloadedState<AppState>) =>
  configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: getDefaultMiddleware => {
      const listMiddlewares = getDefaultMiddleware({
        serializableCheck: false,
      }).concat([...Object.values(middlewares), sagaMiddleware]);
      if (WEBPACK_LOCAL_CONFIG.useLogger) {
        listMiddlewares.push(createLogger({ collapsed: true }) as ThunkMiddleware);
      }
      return listMiddlewares;
    },
  });

export const store = setupStore();

sagaMiddleware.run(sagas);

export default store;
